import React, { useCallback, useState, useRef } from 'react';
import { useClickAway } from 'react-use';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import css from '@emotion/css';
import { getChosenBusiness } from 'store/authentication/selectors';
import { WYSIWYG, useEditor } from './editor';
import { WYSIWYGAIContext, useWYSIWYGAIContext } from './ai-context';
import {
  BasicToolBar,
  FloatingToolbar,
} from '../StandaloneToolBar/presets/WysiwygToolbar';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useBoundingRect } from 'hooks/useBoundingRect';
import { applyRef } from 'components/Inputs/props';

const DEFAULT_LINE_HEIGHT = '1.25';

export const defaultFonts = [
  { label: 'Arial', value: 'Arial' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Helvetica Neue', value: 'Helvetica Neue' },
  { label: 'Lucida Sans Unicode', value: 'Lucida Sans Unicode' },
  { label: 'Tahoma', value: 'Tahoma' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Verdana', value: 'Verdana' },
];

const EditorWrapper = styled.div`
  ${({ borderColor }) => css`
    & > div:first-child > div {
      border-color: ${borderColor};
    }
    & > div:last-child {
      border-color: ${borderColor};
    }
  `}
  &:hover {
    ${({ borderHoverColor }) =>
      borderHoverColor &&
      css`
        & > div:first-child > div {
          border-top-color: ${borderHoverColor};
          border-left-color: ${borderHoverColor};
          border-right-color: ${borderHoverColor};
        }
        & > div:last-child {
          border-color: ${borderHoverColor};
        }
      `}
  }
`;

/**
 * Depending on usages through the application,
 * we can either have preset editors like we see here, or
 * we can have custom editors written up like this in their
 * respective locations in the app.
 */

export const BasicRichTextEditor = React.forwardRef(
  (
    {
      className,
      fieldId,
      onChange,
      onBlur,
      onFocus,
      onInit,
      autofocus,
      placeholder,
      initialValue,
      fonts,
      bottomBarNode,
      defaultFontFamily = 'Arial',
      defaultFontSize = '14px',
      children,
      mentionOptions,
      mergeFields = null,
      mergeFieldSuggestion,

      disabled = false,
      borderColor = 'var(--color-border-medium)',
      borderHoverColor = '',
      textLinkOptions = [],
      scriptExecutionOptions = [],
      fitToContent = false,
      initialHeight = 200,
      minHeight = 100,
      enableResize = true,
      enableAI = false,
      enableFontFamily = false,
      enableFontSize = false,
      enableLineHeight = false,
      defaultLineHeight = DEFAULT_LINE_HEIGHT,
      enableColor = false,
      enableHighlight = false,
      enableLinks = false,
      enableMergeFields = false,
      enableTextAlign = false,
      enableClearFormatting = false,
      enableLists = true,
      enableTextStyles = true,
      enableToolbar = true,
      enableHorizontalRule = false,
      enableHistoryControls = true,
      enableDisallowWhitespace = false,
      enableMentions = false,
      enableAllObjectMergeFields = false,
      unavailableMergeFields = null,
      appendMergeFieldExtraSpace = true,
      onToggleEncoding = null,
      encodeMergeFieldsInBody = true,
      codeBlockLanguage = null,
      disableCodeBlockEscape = true,
      wideMergePicker = false,
      dataQa = 'basic-rich-text-editor',
      error = false,
      preserveWhitespace,
    },
    ref
  ) => {
    const { assignFieldHandle } = useKeyBoardContext();
    const noteRef = useRef();
    const [focused, setFocused] = useState(borderColor);
    const [measureRef, { height }] = useBoundingRect();

    const mergeRef = useCallback(
      (handle) => {
        applyRef(noteRef, handle);
        applyRef(ref, handle);
        if (handle) {
          measureRef(handle.containerRef.current);
        }
      },
      [ref, measureRef]
    );

    assignFieldHandle(fieldId, {
      customFocus: () => {
        noteRef.current?.editor?.commands?.focus();
        return noteRef.current?.editor;
      },
      customEnter: (e) => {
        if (!noteRef.current) {
          setFocused(null);
          return false;
        } else {
          setFocused(
            !(e.ctrlKey + e.metaKey)
              ? 'var(--color-button-secondary-hover)'
              : null
          );
          return !(e.ctrlKey + e.metaKey);
        }
      },
      disabled,
    });

    const handleOnFocus = useCallback(
      (e) => {
        fieldId && setFocused('var(--color-button-secondary-hover)');
        onFocus && onFocus(e);
      },
      [fieldId, onFocus]
    );

    const handleOnBlur = useCallback(
      (e) => {
        fieldId && setFocused(null);
        onBlur && onBlur(e);
      },
      [fieldId, onBlur]
    );

    const business = useSelector(getChosenBusiness);
    const { isDefaultContextValue } = useWYSIWYGAIContext();

    const editorContent = (
      <EditorWrapper
        className={className}
        borderHoverColor={
          !error ? borderHoverColor : 'var(--color-button-warning-hover)'
        }
        borderColor={!error ? focused : 'var(--color-button-warning-hover)'}
        style={{ '--wysiwyg-height': `${height}px` }}
        {...(dataQa && { 'data-qa': dataQa })}
      >
        <WYSIWYG
          fitToContent={fitToContent}
          onChange={onChange}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          onInit={onInit}
          autofocus={autofocus}
          disabled={disabled}
          ref={mergeRef}
          placeholder={placeholder}
          initialValue={initialValue}
          initialHeight={initialHeight}
          minHeight={minHeight}
          bottomBarNode={bottomBarNode}
          defaultFontFamily={defaultFontFamily}
          defaultFontSize={defaultFontSize}
          defaultLineHeight={defaultLineHeight}
          enableResize={enableResize}
          enableAI={enableAI}
          enableFontFamily={enableFontFamily}
          enableFontSize={enableFontSize}
          enableLineHeight={enableLineHeight}
          enableColor={enableColor}
          enableHighlight={enableHighlight}
          enableLinks={enableLinks}
          enableMergeFields={enableMergeFields}
          enableAllObjectMergeFields={enableAllObjectMergeFields}
          unavailableMergeFields={unavailableMergeFields}
          mergeFields={mergeFields}
          mergeFieldSuggestion={mergeFieldSuggestion}
          enableLists={enableLists}
          enableHorizontalRule={enableHorizontalRule}
          enableTextAlign={enableTextAlign}
          enableTextStyles={enableTextStyles}
          enableDisallowWhitespace={enableDisallowWhitespace}
          enableToolbar={enableToolbar}
          enableMentions={enableMentions}
          mentionOptions={mentionOptions}
          codeBlockLanguage={codeBlockLanguage}
          disableCodeBlockEscape={disableCodeBlockEscape}
          preserveWhitespace={preserveWhitespace}
        >
          {enableToolbar ? (
            <BasicToolBar
              enableAI={enableAI}
              enableTextStyles={enableTextStyles}
              enableHorizontalRule={enableHorizontalRule}
              enableColor={enableColor}
              enableHighlight={enableHighlight}
              enableFontSize={enableFontSize}
              defaultFontSize={defaultFontSize}
              enableFontFamily={enableFontFamily}
              fonts={fonts}
              defaultFontFamily={defaultFontFamily}
              enableLineHeight={enableLineHeight}
              defaultLineHeight={defaultLineHeight}
              enableLists={enableLists}
              enableTextAlign={enableTextAlign}
              enableLinks={enableLinks}
              textLinkOptions={textLinkOptions}
              scriptExecutionOptions={scriptExecutionOptions}
              enableClearFormatting={enableClearFormatting}
              enableMergeFields={enableMergeFields}
              enableAllObjectMergeFields={enableAllObjectMergeFields}
              enableHistoryControls={enableHistoryControls}
              mergeFields={mergeFields}
              appendMergeFieldExtraSpace={appendMergeFieldExtraSpace}
              onToggleEncoding={onToggleEncoding}
              encodeMergeFieldsInBody={encodeMergeFieldsInBody}
              wideMergePicker={wideMergePicker}
            />
          ) : null}
          {children}
        </WYSIWYG>
      </EditorWrapper>
    );

    // Only create a new context if enableAI is true, we have a business ID,
    // and there isn't already a context provided by a parent component
    if (enableAI && business?.id && isDefaultContextValue) {
      return (
        <WYSIWYGAIContext businessId={business.id}>
          {editorContent}
        </WYSIWYGAIContext>
      );
    }

    return editorContent;
  }
);

export const BasicPlainTextEditor = ({
  onChange,
  onBlur,
  onFocus,
  autofocus,
  placeholder,
  initialValue,
  children,
  codeBlockLanguage,
  disableCodeBlockEscape,
}) => (
  <WYSIWYG
    fitToContent={false}
    onChange={onChange}
    onBlur={onBlur}
    onFocus={onFocus}
    autofocus={autofocus}
    placeholder={placeholder}
    initialValue={initialValue}
    enableResize={false}
    codeBlockLanguage={codeBlockLanguage}
    disableCodeBlockEscape={disableCodeBlockEscape}
  >
    {children}
  </WYSIWYG>
);

/**
 * @remarks `zIndex`, `maxToolbarRows`, and `target` are only used when `asOverlay` is true.
 * Special care must be taken to ensure the onBlur callback is called only when the toolbar active state
 * will be set to false (close toolbar). Interacting with dropdowns and popups (color pickers) within the
 * toolbar will steal focus and cause the text input's blur event to fire. In these cases (when toolbarActive
 * is set to true), we save the blur event's arguments to manually call onBlur in handleClickAway. blurArgs are
 * cleared on focus to avoid calling onBlur twice.
 */
export const FloatingRichTextEditor = React.forwardRef(
  (
    {
      onChange,
      onBlur,
      onFocus,
      autofocus = false,
      placeholder,
      initialValue,
      fonts,
      defaultFontFamily,
      defaultFontSize,
      defaultLineHeight = DEFAULT_LINE_HEIGHT,
      children,
      zIndex,
      maxToolbarRows,
      onFontFamilyChange,
      asOverlay = false,
      persistBorder = false,
      textLinkOptions = [],
      scriptExecutionOptions = [],
      enableAI = false,
      enableFontFamily = false,
      enableFontSize = false,
      enableLineHeight = false,
      enableColor = false,
      enableHighlight = false,
      enableHistoryControls = true,
      enableLinks = false,
      enableMergeFields = false,
      enableAllObjectMergeFields = false,
      unavailableMergeFields = null,
      mergeFields = null,
      enableClearFormatting = false,
      allowRelativeLinks = false,
      wideMergePicker = false,
      mergeFieldSuggestion,
    },
    ref
  ) => {
    const editorRef = useRef();
    const blurArgs = useRef(null);
    const toolbarActive = useRef(false);
    const [editorActive, setEditorActive] = useState(autofocus);

    const refCallback = useCallback(
      (editor) => {
        ref.current = editor;
        editorRef.current = editor;
      },
      [ref]
    );

    const handleFocus = useCallback(
      (args) => {
        toolbarActive.current = false;
        blurArgs.current = null;
        setEditorActive(true);
        onFocus?.(args);
      },
      [onFocus]
    );

    const handleBlur = useCallback(
      (args) => {
        if (!toolbarActive.current) {
          onBlur?.(args);
        } else {
          blurArgs.current = args;
        }
      },
      [onBlur]
    );

    const handleClickAway = useCallback(() => {
      if (editorActive && !toolbarActive.current) {
        setEditorActive(false);
        if (blurArgs.current) {
          handleBlur(blurArgs.current);
        }
      }
    }, [handleBlur, editorActive]);

    useClickAway(editorRef.current?.containerRef ?? {}, handleClickAway, [
      'mousedown',
    ]);

    return (
      <WYSIWYG
        floating
        ref={refCallback}
        onChange={onChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        showBorder={persistBorder || editorActive}
        autofocus={autofocus}
        zIndex={zIndex}
        placeholder={placeholder}
        initialValue={initialValue}
        defaultFontFamily={defaultFontFamily}
        defaultFontSize={defaultFontSize}
        defaultLineHeight={defaultLineHeight}
        enableAI={enableAI}
        enableFontFamily={enableFontFamily}
        enableLineHeight={enableLineHeight}
        enableFontSize={enableFontSize}
        enableColor={enableColor}
        enableHighlight={enableHighlight}
        enableLinks={enableLinks}
        enableMergeFields={enableMergeFields}
        enableAllObjectMergeFields={enableAllObjectMergeFields}
        unavailableMergeFields={unavailableMergeFields}
        mergeFields={mergeFields}
        mergeFieldSuggestion={mergeFieldSuggestion}
        enableLists
        enableTextAlign
        enableTextStyles
      >
        {editorActive && (
          <FloatingToolbar
            target={ref.current?.containerRef?.current}
            asOverlay={asOverlay}
            zIndex={zIndex}
            maxToolbarRows={maxToolbarRows}
            toolbarActive={toolbarActive}
            enableColor={enableColor}
            enableHighlight={enableHighlight}
            enableFontSize={enableFontSize}
            defaultFontSize={defaultFontSize}
            enableLineHeight={enableLineHeight}
            defaultLineHeight={defaultLineHeight}
            enableFontFamily={enableFontFamily}
            enableHistoryControls={enableHistoryControls}
            fonts={fonts}
            enableAI={enableAI}
            defaultFontFamily={defaultFontFamily}
            enableLinks={enableLinks}
            textLinkOptions={textLinkOptions}
            scriptExecutionOptions={scriptExecutionOptions}
            enableClearFormatting={enableClearFormatting}
            enableMergeFields={enableMergeFields}
            enableAllObjectMergeFields={enableAllObjectMergeFields}
            mergeFields={mergeFields}
            onFontFamilyChange={onFontFamilyChange}
            allowRelativeLinks={allowRelativeLinks}
            wideMergePicker={wideMergePicker}
          />
        )}
        {children}
      </WYSIWYG>
    );
  }
);

export const mergeFieldsOptionsToItems = (mergeFields) =>
  mergeFields.map(({ key, label, options: items }) => ({ key, label, items }));

export * from './ai-context';
export { useEditor };
export { TextViewer } from './viewer';
